import { type ComponentType, lazy, type LazyExoticComponent } from 'react';
import { logger } from './logger';

/** @desc How long a failed dynamic import waits before its one retry. */
export const LAZY_RETRY_DELAY_MS = 800;

export type RetryOptions = {
  delayMs?: number;
  /** @desc The pause before the retry; a test passes one that resolves at once. */
  wait?: (ms: number) => Promise<void>;
};

const pause = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * @desc The messages a failed chunk load arrives with: the three browsers' dynamic-import
 * failures (Chrome, Firefox, Safari — the same three TanStack Router's own reload check reads)
 * and Vite's CSS preload failure. Lets a fallback say "the portal may have been updated" rather
 * than "something broke".
 */
const CHUNK_LOAD_MESSAGES = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
];

export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error && CHUNK_LOAD_MESSAGES.some((prefix) => error.message.startsWith(prefix));

/**
 * @desc Runs a dynamic import, and if it rejects, runs it once more after a short pause; a second
 * failure is thrown to the caller (a Suspense boundary's error boundary, a route's
 * `errorComponent`). One retry covers the network blip; a chunk that is gone for good — a
 * deployment replaced its hash — fails twice, and recovering from that is the
 * `vite:preloadError` handler's job (`installPreloadErrorReload`), not this one's.
 *
 * The retry calls the same `importFn`, so it requests the same URL. A browser that caches a
 * failed module fetch rejects the retry at once, which still ends in the boundary rather than a
 * hang.
 */
export async function retryableImport<T>(
  importFn: () => Promise<T>,
  { delayMs = LAZY_RETRY_DELAY_MS, wait = pause }: RetryOptions = {},
): Promise<T> {
  try {
    return await importFn();
  } catch (error) {
    logger.warn('[lazy] Dynamic import failed, retrying once', {
      error: error instanceof Error ? error.message : String(error),
      delayMs,
    });
    await wait(delayMs);
    return importFn();
  }
}

/**
 * @desc `React.lazy` over {@link retryableImport}: the component's chunk is retried once before
 * the failure reaches the nearest error boundary. Wrap the result in `LazyBoundary` (or render it
 * under a route, whose `errorComponent` is the boundary) so that failure has a Reload affordance.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the same constraint `React.lazy` declares; props are the component's own
export function retryableLazy<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  options?: RetryOptions,
): LazyExoticComponent<T> {
  return lazy(() => retryableImport(importFn, options));
}
